import React, {useEffect, useState} from 'react';
import {message, Button} from 'antd';
import {ReportFileInitialValue} from "./config/reportFileFilterOptions";
import {ReportFileFiltersType} from "./hooks/useWmsReportFileQuery";
import {ReportFileType} from "./graphql/queries/getReportFileListingForWms";
import {useWmsReportFileQuery} from "./hooks/useWmsReportFileQuery";
import {useReportOperators} from "./hooks/useReportOperators";
import WmsReportFileFilter from "./components/WmsReportFileFilter";
import WmsReportFileColumns from "./components/WmsReportFileColumns";
import { useTranslation } from 'react-i18next';
import { useLazyQuery } from '@apollo/client';
import {
    GET_BATCH_REPORT_DOWNLOAD_URLS,
    GetBatchReportDownloadUrlsResponse,
    GetBatchReportDownloadUrlsVariables
} from './graphql/queries/getBatchReportFileUrls';
import TablePaginationBar from '../../../components/TablePaginationBar';
import { getLastWeekDateRange } from '../../../utils/dateTimeUtils';
import { useWarehouseContext } from '../../../contexts/WarehouseContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { ReportFileTypeEnum } from '../../../enums/ReportFileEnum';
import { DELIVERY_DOWNLOAD_FROM_PARTNER_PENALTY_STATE_KEY } from '../../../constants/deliveryDownloadNavigation';

const WmsReportFilePage: React.FC = () => {
    const { currentWarehouse } = useWarehouseContext();
    const location = useLocation();
    const navigate = useNavigate();
    // 从「罚单管理中心」下载弹窗跳过来时，默认筛选「罚单管理中心导出」
    const fromPartnerPenalty =
        (location.state as Record<string, unknown> | null)?.[DELIVERY_DOWNLOAD_FROM_PARTNER_PENALTY_STATE_KEY] === true;
    const initialFilters: ReportFileFiltersType = fromPartnerPenalty
        ? { ...ReportFileInitialValue, type: ReportFileTypeEnum.PARTNER_PENALTY_EXPORT }
        : ReportFileInitialValue;
    const warehouseId = currentWarehouse?.id ? Number(currentWarehouse.id) : 0;
    const [tempFilters, setTempFilters] = useState<ReportFileFiltersType>(initialFilters);
    const [filters, setFilters] = useState<ReportFileFiltersType>(initialFilters);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [selectedRows, setSelectedRows] = useState<ReportFileType[]>([]);
    
    // 先获取操作人数据和默认过滤条件
    const {
        operatorOptions,
        loading: operatorsLoading,
        defaultFilters
    } = useReportOperators(warehouseId);
    
    // 只有在操作人数据加载完成后才执行报表查询
    // - undefined：未初始化，默认最近一周
    // - null：用户手动清空（点击叉号），保持为空
    const getEffectiveReportDate = (reportDate: [string | null, string | null] | null | undefined): [string | null, string | null] | null => {
        if (reportDate === null) return null;
        const isValid = reportDate &&
            Array.isArray(reportDate) &&
            reportDate.length === 2 &&
            reportDate[0] !== null &&
            reportDate[1] !== null;
        return isValid ? reportDate : getLastWeekDateRange() as [string | null, string | null];
    };

    const {
        reportFileData,
        loading: reportLoading,
        refetch,
        totalElements,
        buildQueryVariables
    } = useWmsReportFileQuery(
        {
            ...filters,
            warehouseId,
            reportDate: getEffectiveReportDate(filters.reportDate)
        },
        currentPage,
        pageSize,
        !defaultFilters || !warehouseId
    );

    const handleSelectChange = (newSelectedRowKeys: React.Key[], newSelectedRows: ReportFileType[]) => {
        setSelectedRowKeys(newSelectedRowKeys);
        setSelectedRows(newSelectedRows);
    };

    // 当默认过滤条件加载完成时，初始化过滤器状态
    useEffect(() => {
        if (defaultFilters) {
            const filtersToSet = {
                ...defaultFilters,
                // 默认日期不写入 URL/hash：保持 undefined
                reportDate: undefined,
                // defaultFilters 晚于首屏返回，这里要把罚单导出类型带回来，否则会被覆盖成默认类型
                ...(fromPartnerPenalty ? { type: ReportFileTypeEnum.PARTNER_PENALTY_EXPORT } : {})
            };
            if (!filters.operatorId) {
                setTempFilters(filtersToSet);
                setFilters(filtersToSet);
            }
            // 消费掉一次性的 state，避免刷新/后退时又被预设成罚单导出
            if (fromPartnerPenalty) {
                navigate('.', { replace: true, state: {} });
            }
        }
    }, [defaultFilters]);

    const handleResetFilters = () => {
        const resetFilters = defaultFilters || ReportFileInitialValue;
        setTempFilters(resetFilters);
        setFilters(resetFilters);
        setCurrentPage(1);
    };

    const handleFilter = () => {
        setFilters(tempFilters);
        setCurrentPage(1);
    };

    // 添加useEffect监听状态变化后执行查询
    useEffect(() => {
        refetch(buildQueryVariables());
    }, [filters, currentPage, pageSize]);
    
    // 处理分页变化
    const handlePageChange = (page: number, pageSize: number) => {
        setCurrentPage(page);
        setPageSize(pageSize);
    };

    const { t } = useTranslation();
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [getBatchDownloadUrls] = useLazyQuery<
        GetBatchReportDownloadUrlsResponse,
        GetBatchReportDownloadUrlsVariables
    >(GET_BATCH_REPORT_DOWNLOAD_URLS);

    // 生成文件名
    const generateFilename = (index: number, reportName?: string) => {
        const timestamp = new Date().toISOString().split('T')[0];
        if (reportName) {
            const hasXlsx = /\.xlsx$/i.test(reportName);
            return hasXlsx ? reportName : `${reportName}.xlsx`;
        }
        return `Report_${index + 1}_${timestamp}.xlsx`;
    };

    const downloadFile = async (url: string, filename: string) => {
        const res = await fetch(url);
        const blob = await res.blob();
        const blobUrl = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(blobUrl);
    };

    // 处理批量下载
    const handleBatchDownload = async () => {
        if (selectedRows.length === 0) {
            message.warning('Please select the report you wish to download');
            return;
        }

        setDownloadLoading(true);
        try {
            const reportIds = selectedRows.map(report => parseInt(report.id.toString(), 10));

            const { data } = await getBatchDownloadUrls({
                variables: { reportIds, warehouseId },
                fetchPolicy: 'network-only'
            });

            if (!data?.getBatchReportDownloadUrlsForWms) {
                message.error('Failed to obtain the download link.');
                return;
            }

            const downloadUrls = data.getBatchReportDownloadUrlsForWms;
            
            if (downloadUrls.length === 0) {
                message.warning('No available download link is available.');
                return;
            }

            if (downloadUrls.length > 1) {
                message.info(`Starting batch download of ${downloadUrls.length} files...`);
            }

            for (let i = 0; i < downloadUrls.length; i++) {
                const url = downloadUrls[i];
                const reportName = i < selectedRows.length ? selectedRows[i].name : undefined;
                const filename = generateFilename(i, reportName);

                try {
                    await downloadFile(url, filename);
                    await new Promise(res => setTimeout(res, 500));
                    message.success(`File ${i + 1} downloaded`);
                } catch (e) {
                    console.error(`Failed to download file ${i + 1}:`, e);
                    message.error(`Failed to download file ${i + 1}`);
                }
            }

            message.success(`All ${downloadUrls.length} files download initiated successfully!`);

        } catch (error) {
            console.error('Batch download failed:', error);
            message.error('Batch download failed, please try again later');
        } finally {
            setDownloadLoading(false);
        }
    };

    return (
        <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
            {/* 筛选条件 */}
            <WmsReportFileFilter
                filters={tempFilters}
                setFilters={setTempFilters}
                handleFilter={handleFilter}
                handleResetFilters={handleResetFilters}
                selectedRows={selectedRows}
                operatorOptions={operatorOptions}
            />

            {/* 批量操作区域 */}
            <div className="flex justify-between items-center mb-3 pl-0">
                {/* 左侧 */}
                <div className="flex items-center gap-2">
                </div>

                {/* 右侧 */}
                <div className="flex items-center gap-2">
                    <Button
                        type="primary"
                        onClick={handleBatchDownload}
                        loading={downloadLoading}
                        disabled={selectedRows.length === 0}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{t('components.button.download')} ({selectedRows.length})</span>
                    </Button>
                    <Button type="primary" onClick={handleFilter} className="w-auto min-w-24">
                        <span className="font-bold">{t('wms.download.filters.filter')}</span>
                    </Button>
                    <Button onClick={handleResetFilters} className="w-auto min-w-24">
                        <span className="font-bold">{t('wms.download.filters.reset')}</span>
                    </Button>
                </div>
            </div>

            {/* 报表列表 */}
            <WmsReportFileColumns
                reportFileData={reportFileData}
                onSelectChange={handleSelectChange}
                selectedRowKeys={selectedRowKeys}
                loading={operatorsLoading || reportLoading}
            />

            {/* 分页 */}
            <TablePaginationBar
                total={totalElements}
                current={currentPage}
                pageSize={pageSize}
                onChange={handlePageChange}
            />
        </div>
    );
};

export default WmsReportFilePage;

